export type StaticPageSlug = "privacy" | "terms" | "commercial-disclosure";

export type LegalSection = {
  heading: string;
  paragraphs?: string[];
  items?: string[];
  rows?: Array<{ label: string; value: string }>;
};

export type LegalPageContent = {
  slug: StaticPageSlug;
  title: string;
  lead?: string;
  effectiveDate: string;
  sections: LegalSection[];
};

export const staticPagePathMap: Record<StaticPageSlug, string> = {
  privacy: "/privacy",
  terms: "/terms",
  "commercial-disclosure": "/legal/commercial-disclosure"
};

export const legalPages: Record<StaticPageSlug, LegalPageContent> = {
  privacy: {
    slug: "privacy",
    title: "プライバシーポリシー",
    lead: "本ポリシーは、ハコフィットにおけるユーザー情報の取扱いを定めるものです。",
    effectiveDate: "制定日：2026年4月25日",
    sections: [
      {
        heading: "1. 事業者情報",
        items: [
          "事業者名：神谷 悠介（個人事業主）",
          "サービス名：ハコフィット（Hakofit）",
          "お問い合わせ窓口：本サービス内のお問い合わせページ",
          "所在地・電話番号：特定商取引法第11条ただし書に基づき、省略しております。開示を希望される場合は、お問い合わせページからご請求ください。遅滞なく書面または電子メールにより開示します。"
        ]
      },
      {
        heading: "2. 適用範囲",
        paragraphs: [
          "本プライバシーポリシーは、本サービスおよび本サイトにおいて事業者が取得するユーザー情報の取扱いに適用されます。"
        ]
      },
      {
        heading: "3. 取得する情報の内容",
        paragraphs: [
          "事業者は、本サービスの提供にあたり、主に次の情報を取得することがあります。"
        ],
        items: [
          "利用に伴い自動的に取得される情報：IPアドレス、ブラウザ情報、OS情報、端末情報、アクセス日時、操作履歴、CookieやlocalStorage等の識別子",
          "設計・出力に関する情報：設計データのハッシュ、設計タイプ、決済前後の確認に必要なメタデータ。3Dモデルや設計データそのものは原則としてユーザーのブラウザ内で生成されますが、問い合わせや不具合調査のためユーザーが任意に提供した場合は取得することがあります。",
          "決済関連情報：クレジットカード情報は決済代行事業者であるStripe等が管理し、事業者はカード番号全体を保持・参照しません。事業者はCheckout Session ID、決済金額、決済日時、決済ステータス等を取得することがあります。",
          "問い合わせ時に取得する情報：問い合わせ内容、回答に必要な連絡先、スクリーンショット、利用環境等",
          "ログ情報および診断情報：アプリケーションのエラーログ、パフォーマンス情報、セキュリティ関連ログ"
        ]
      },
      {
        heading: "4. 利用目的",
        items: [
          "本サービスの提供、運営、決済確認、3Dプリント用データの出力、再出力キーの発行のため",
          "不具合調査、品質向上、機能改善、利用状況の分析のため",
          "問い合わせ対応、重要なお知らせ、規約やポリシー変更の通知のため",
          "不正利用、不正決済、セキュリティ上の問題の検知および防止のため",
          "法令または公的機関からの要請に対応するため"
        ]
      },
      {
        heading: "5. Cookie等の利用について",
        items: [
          "本サービスでは、決済前の設計データの一時保存、決済後の復元、利便性向上、アクセス解析等のためにCookie、localStorageその他の技術を利用することがあります。",
          "ユーザーはブラウザの設定によりCookie等を制限できますが、その場合、決済後のダウンロードや一部機能が正常に動作しないことがあります。",
          "アクセス解析サービスを利用する場合は、本サイト上での告知または本ポリシーの改定により、取得情報の概要を明示します。"
        ]
      },
      {
        heading: "6. 第三者提供について",
        items: [
          "事業者は、本人の同意がある場合、法令に基づく場合、人の生命・身体・財産の保護に必要な場合、その他法令で認められる場合を除き、取得した個人情報を第三者に提供しません。",
          "統計的に処理されたデータなど、個人を識別できない形に加工した情報については、サービス改善や公開資料等に利用することがあります。"
        ]
      },
      {
        heading: "7. 委託について",
        paragraphs: [
          "事業者は、サービス運営、決済処理、ホスティング、問い合わせ対応、エラーログ収集等のため、個人情報の取扱いの全部または一部を外部事業者に委託することがあります。この場合、委託先に対し適切な監督を行います。"
        ],
        items: [
          "クレジットカード決済等を提供する決済代行事業者",
          "アプリケーションのホスティング、配信、セキュリティ機能を提供するクラウドインフラ事業者",
          "エラーログ収集、アクセス解析、問い合わせ対応等を提供するサービス事業者"
        ]
      },
      {
        heading: "8. 安全管理措置",
        items: [
          "通信の暗号化（HTTPS/SSL等）",
          "秘密鍵、APIキー、認証情報の適切な管理",
          "アクセス権限の管理",
          "不正アクセス防止のための技術的対策",
          "個人情報の取扱いに関するルールの整備"
        ]
      },
      {
        heading: "9. 保有期間",
        paragraphs: [
          "事業者は、取得した情報を、利用目的の達成に必要な範囲内で保有します。法令上の義務履行、決済確認、紛争対応、不正利用防止等のために必要な範囲で、一定期間情報を保有することがあります。"
        ]
      },
      {
        heading: "10. 開示・訂正・利用停止等の請求",
        items: [
          "ユーザーは、事業者が保有する自己の個人情報について、開示、訂正、追加、削除、利用停止等を求めることができます。",
          "請求を行う場合は、本サービス内のお問い合わせページからご連絡ください。本人確認のため、追加の情報提供をお願いする場合があります。",
          "事業者は、法令に基づき、合理的な範囲で速やかに対応します。ただし、法令により開示義務を負わない場合や、請求が過度である場合等には、請求に応じられないことがあります。"
        ]
      },
      {
        heading: "11. 未成年の利用者について",
        paragraphs: [
          "未成年のユーザーが本サービスを利用する場合、必ず親権者その他の法定代理人の同意を得たうえでご利用ください。"
        ]
      },
      {
        heading: "12. プライバシーポリシーの変更",
        paragraphs: [
          "事業者は、必要に応じて本プライバシーポリシーの内容を変更することがあります。重要な変更を行う場合には、本サイト上での掲示その他の適切な方法により通知します。"
        ]
      },
      {
        heading: "13. お問い合わせ窓口",
        paragraphs: [
          "本プライバシーポリシーおよび個人情報の取扱いに関するお問い合わせ、開示等の請求は、本サービス内のお問い合わせページよりご連絡ください。"
        ]
      }
    ]
  },
  terms: {
    slug: "terms",
    title: "利用規約",
    lead: "本利用規約は、ハコフィットの提供条件および利用に関する権利義務関係を定めるものです。",
    effectiveDate: "制定日：2026年4月25日",
    sections: [
      {
        heading: "第1条（本規約の適用）",
        items: [
          "本規約は、本サービスの提供および利用に関する一切の関係に適用されます。",
          "事業者は、本サービスに関して、本規約のほかに個別規定、ガイドライン、注意書き等を定める場合があります。これらは本規約の一部を構成します。"
        ]
      },
      {
        heading: "第2条（定義）",
        items: [
          "「本サービス」とは、ハコフィット（Hakofit）として提供される、収納用の箱、トレー、仕切り等の3Dプリント用データをブラウザ上で作成・出力するサービスをいいます。",
          "「ユーザー」とは、本サービスにアクセスし、または本サービスを利用するすべての者をいいます。",
          "「出力データ」とは、本サービスを通じて生成されるSTLファイル、設計データ、README、再出力キー等をいいます。",
          "「外部プリントサービス」とは、ユーザーが出力データを使って造形を依頼するDMM.make、JLC3DPその他の3Dプリント代行サービス、またはユーザー自身の3Dプリンターをいいます。"
        ]
      },
      {
        heading: "第3条（規約への同意）",
        items: [
          "ユーザーは、本規約に同意した場合に限り本サービスを利用することができます。",
          "ユーザーが本サービスを実際に利用した時点で、本規約に同意したものとみなします。",
          "未成年のユーザーが本サービスを利用する場合、親権者その他の法定代理人の同意を得たうえで利用するものとします。"
        ]
      },
      {
        heading: "第4条（サービス内容）",
        items: [
          "本サービスは、ユーザーが入力した寸法やオプションに基づき、収納用の箱、トレー、仕切り等の3Dプリント用データを生成するブラウザサービスです。",
          "本サービスは、完成品の収納用品を販売・配送するものではありません。",
          "ユーザーは、生成された出力データを、自身の3Dプリンターまたは外部プリントサービスに入稿して造形できます。",
          "本サービスは、造形費、送料、外部プリントサービスの納期、素材、造形可否、仕上がり品質を保証するものではありません。"
        ]
      },
      {
        heading: "第5条（料金・支払方法）",
        items: [
          "本サービスの有料出力には、データ作成料が発生します。現在のデータ作成料は税込550円です。ただし、価格を変更する場合は、本サイト上または決済画面に表示する金額を優先します。",
          "データ作成料は、STL生成および設計データ作成の対価であり、外部プリントサービスの造形費、送料、税金、素材費、その他費用を含みません。",
          "ユーザーは、データ作成料を事業者が指定する決済手段（クレジットカード決済等）により支払うものとします。",
          "決済処理はStripe等の決済代行事業者を通じて行われます。"
        ]
      },
      {
        heading: "第6条（出力データと再出力）",
        items: [
          "決済完了後、ユーザーは出力データをZIPファイルとしてダウンロードできます。",
          "出力データには、主に3Dプリント用のSTLファイル、設計データ、再出力キー、アップロード案内が含まれます。",
          "再出力キーは、同一設計データについて一定期間の再出力確認に利用されます。寸法、仕切り、取手等を変更した別設計の出力には、新たな決済が必要です。再出力キーや設計データを紛失した場合、自動復元できないことがあります。",
          "ブラウザのキャッシュ削除、端末変更、ダウンロードファイルの紛失等により生じた不利益について、事業者は責任を負いません。"
        ]
      },
      {
        heading: "第7条（3Dプリントおよび適合性について）",
        items: [
          "ユーザーは、造形前に寸法、肉厚、底厚、仕切り、切り欠き、用途、素材、造形方式、外部プリントサービスの入稿条件を自ら確認するものとします。",
          "本サービスは、出力データがすべてのプリンター、素材、外部プリントサービスで造形可能であること、または造形物がユーザーの収納場所や用途に完全に適合することを保証しません。",
          "造形物の強度、耐熱性、耐薬品性、食品接触への適性、子どもやペットの安全性等については、ユーザーの責任で確認してください。",
          "外部プリントサービスとの契約、支払い、配送、返品、問い合わせは、ユーザーと当該外部プリントサービスとの間で行われるものとします。"
        ]
      },
      {
        heading: "第8条（禁止事項）",
        items: [
          "法令または公序良俗に違反する行為",
          "犯罪行為または危険物、武器、違法物品の作成に関連する行為",
          "他者の知的財産権、肖像権、プライバシー権、名誉権その他の権利・利益を侵害する行為",
          "本サービスを解析、改変、リバースエンジニアリングする行為",
          "本サービスのサーバー、ネットワーク、決済機能に過度な負荷をかける行為",
          "不正アクセス、不正決済、再出力キーの改ざん、またはこれらを試みる行為",
          "事業者が不適切と判断する行為"
        ]
      },
      {
        heading: "第9条（知的財産権と出力データの利用）",
        items: [
          "本サイトおよび本サービスに関するプログラム、デザイン、ロゴ、テキスト、画像等の知的財産権は、事業者または正当な権利者に帰属します。",
          "ユーザーは、正当に決済して取得した出力データを、自身のために保存、編集、3Dプリント、外部プリントサービスへ入稿することができます。",
          "ユーザーは、事業者の許可なく、本サービスのテンプレート、出力ロジック、UI、出力データを利用して競合サービスを構築したり、再配布・販売したりしてはなりません。",
          "ユーザーが入力した寸法や用途に関する情報に第三者の権利が含まれる場合、ユーザーの責任で必要な権利処理を行うものとします。"
        ]
      },
      {
        heading: "第10条（サービスの変更・中断・終了）",
        items: [
          "事業者は、保守、障害対応、外部サービスの停止、セキュリティ上の必要、その他やむを得ない事情により、本サービスの全部または一部を変更、中断、終了することがあります。",
          "事業者は、本条に基づく変更、中断、終了によりユーザーに生じた損害について、事業者の故意または重過失がある場合を除き責任を負いません。"
        ]
      },
      {
        heading: "第11条（免責）",
        items: [
          "本サービスは現状有姿で提供されます。事業者は、本サービスに事実上または法律上の瑕疵がないことを保証しません。",
          "事業者は、出力データの完全性、正確性、造形可能性、特定目的への適合性、外部プリントサービスでの受付可否、造形物の品質や安全性を保証しません。",
          "事業者は、ユーザーの端末、通信環境、ブラウザ、WebGL対応状況、ダウンロード設定等に起因して本サービスが正常に動作しない場合について責任を負いません。",
          "事業者が責任を負う場合であっても、事業者の賠償責任の範囲は、当該取引でユーザーが事業者に支払った金額を上限とします。ただし、事業者に故意または重過失がある場合を除きます。"
        ]
      },
      {
        heading: "第12条（返金）",
        paragraphs: [
          "出力データはデジタルコンテンツであるため、決済完了後の返品、キャンセル、返金は原則として受け付けません。ただし、重複決済、決済完了後に出力データを取得できないなど、事業者が必要と判断した場合または法令上必要な場合はこの限りではありません。"
        ]
      },
      {
        heading: "第13条（個人情報の取扱い）",
        paragraphs: [
          "本サービスにおけるユーザー情報の取扱いについては、別途定めるプライバシーポリシーによるものとします。"
        ]
      },
      {
        heading: "第14条（規約の変更）",
        paragraphs: [
          "事業者は、必要と判断した場合、本規約を変更することがあります。変更後の規約は、本サイト上に掲示した時点から効力を生じるものとします。"
        ]
      },
      {
        heading: "第15条（通知・連絡）",
        paragraphs: [
          "本サービスに関する事業者からユーザーへの通知は、本サイトへの掲示、メール送信その他事業者が適当と判断する方法により行います。ユーザーから事業者への連絡は、本サービス内のお問い合わせページまたは事業者が指定する連絡先宛に行うものとします。"
        ]
      },
      {
        heading: "第16条（準拠法・管轄裁判所）",
        items: [
          "本規約の解釈および適用については、日本法を準拠法とします。",
          "本サービスに関して事業者とユーザーとの間に紛争が生じた場合には、事業者の住所地を管轄する地方裁判所または簡易裁判所を第一審の専属的合意管轄裁判所とします。"
        ]
      }
    ]
  },
  "commercial-disclosure": {
    slug: "commercial-disclosure",
    title: "特定商取引法に基づく表記",
    lead: "通信販売に関する表示事項を以下のとおり定めます。",
    effectiveDate: "制定日：2026年4月25日",
    sections: [
      {
        heading: "表示事項",
        rows: [
          { label: "販売業者", value: "神谷 悠介（個人事業主）" },
          { label: "運営統括責任者", value: "神谷 悠介" },
          { label: "所在地・電話番号", value: "特定商取引法第11条ただし書に基づき、省略しております。請求があり次第提供いたしますので、必要な方は本サービス内のお問い合わせページよりご連絡ください。" },
          { label: "メールアドレス", value: "contact@hakofit.com" },
          { label: "販売価格", value: "データ出力料：税込550円。価格を変更する場合は、購入手続き画面に表示する金額を優先します。" },
          { label: "商品代金以外の必要料金", value: "インターネット接続料金、通信料金等はユーザーの負担となります。外部プリントサービスを利用する場合の造形費、送料、税金、素材費、その他費用は、各外部プリントサービス側で確定し、ユーザーの負担となります。" },
          { label: "支払方法", value: "クレジットカード決済等（Stripe）" },
          { label: "代金の支払時期", value: "購入手続き時に決済が行われます。" },
          { label: "引渡時期", value: "決済完了後、支払い情報の確認が完了次第、ブラウザ上でZIPファイルとしてダウンロード可能になります。" },
          { label: "返品・交換・キャンセル", value: "デジタルコンテンツの性質上、決済完了後の返品、交換、キャンセル、返金は原則としてお受けしておりません。ただし、重複決済や決済完了後に出力データを取得できない場合など、事業者が必要と判断した場合または法令上必要な場合はこの限りではありません。" },
          { label: "動作環境", value: "推奨ブラウザ：Google Chrome、Safari、Firefox、Microsoft Edgeの最新版。3DプレビューにはWebGL対応環境が必要です。" },
          { label: "外部プリントサービスについて", value: "本サービスは3Dプリント用データを作成するサービスであり、造形サービス、配送、素材品質、納期、造形可否を提供・保証するものではありません。外部プリントサービスとの契約はユーザーと当該事業者との間で成立します。" }
        ]
      }
    ]
  }
};
